import { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Edit3, Megaphone } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", description: "", type: "promotional", multiplier: 1.0, start_date: "", end_date: "", active: true };

export default function AdminCampaigns() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => setItems((await api.get("/admin/campaigns")).data);
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const payload = { ...form, multiplier: Number(form.multiplier),
      start_date: new Date(form.start_date || Date.now()).toISOString(),
      end_date: new Date(form.end_date || Date.now()).toISOString() };
    try {
      if (editing) await api.put(`/admin/campaigns/${editing}`, payload);
      else await api.post("/admin/campaigns", payload);
      setOpen(false); setEditing(null); setForm(empty); await load();
      toast.success("Campaign saved");
    } catch { toast.error("Save failed"); }
  };

  const edit = (c) => { setEditing(c.id); setForm({ ...c, start_date: c.start_date.split("T")[0], end_date: c.end_date.split("T")[0] }); setOpen(true); };
  const remove = async (id) => { await api.delete(`/admin/campaigns/${id}`); await load(); };

  return (
    <AdminShell title="Campaigns">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-campaign-btn" className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]"><Plus className="h-4 w-4 mr-1" /> New campaign</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">{editing ? "Edit campaign" : "New campaign"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input data-testid="cmp-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label>Description</Label><Textarea data-testid="cmp-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="cmp-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["promotional", "seasonal", "health_awareness", "corporate"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Multiplier</Label><Input data-testid="cmp-mult" type="number" step="0.1" value={form.multiplier} onChange={(e) => setForm({ ...form, multiplier: e.target.value })} /></div>
                <div className="flex items-end gap-2"><Switch data-testid="cmp-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start</Label><Input data-testid="cmp-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                <div><Label>End</Label><Input data-testid="cmp-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button data-testid="cmp-save-btn" onClick={submit} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-mr rounded-2xl p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="h-10 w-10 rounded-xl bg-[#E2E8F0] flex items-center justify-center"><Megaphone className="h-5 w-5 text-mr-accent" /></div>
              <div className="flex gap-2">
                <Badge variant="outline" className="rounded-full border-mr">{c.type}</Badge>
                {!c.active && <Badge className="rounded-full bg-mr-secondary text-white">Inactive</Badge>}
              </div>
            </div>
            <h3 className="font-display text-lg font-semibold text-mr-primary">{c.title}</h3>
            <p className="text-mr-secondary text-sm mt-1 line-clamp-3">{c.description}</p>
            <div className="mt-3 text-xs text-mr-secondary">{new Date(c.start_date).toLocaleDateString()} → {new Date(c.end_date).toLocaleDateString()}</div>
            <div className="mt-3 flex items-center justify-between">
              <Badge className="rounded-full bg-mr-accent text-white">{c.multiplier}× pts</Badge>
              <div className="flex gap-1">
                <Button data-testid={`cmp-edit-${c.id}`} variant="ghost" size="icon" onClick={() => edit(c)}><Edit3 className="h-4 w-4 text-mr-primary" /></Button>
                <Button data-testid={`cmp-del-${c.id}`} variant="ghost" size="icon" onClick={() => remove(c.id)}><Trash2 className="h-4 w-4 text-mr-accent" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </AdminShell>
  );
}
